import React, { useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react";
import { withRouter } from "react-router-dom";
import deepResearchStore from "Stores/deepResearchData";
import userDetail from "Stores/userDetail";
import { confirmationPopupState } from "Components/common/confirmationPopup";
import { toasterState } from "Components/common/toaster";
import SellerProfileFormBody, {
  buildEmptyProfile,
  hydrateProfileForEdit
} from "Pages/Dashboard/DeepResearch/components/SellerProfileFormBody";
import SellerIntelligencePanel from "Pages/Dashboard/DeepResearch/components/SellerIntelligencePanel";
import { PdfDownloadButton, PdfPrintHeader } from "Components/common/PdfExport";
import SellerProfilePrintDoc from "./SellerProfilePrintDoc";

// Stable JSON for dirty comparison (key order independent enough for our use).
const stableJson = (v) => {
  try {
    return JSON.stringify(v);
  } catch (e) {
    return "";
  }
};

// Create-flow prefill: seller name from the logged-in user, and profile name
// defaulting to the product name (covers website-autofill drafts that arrive
// with product_name already set — the form's live mirror only handles typing).
// Never overwrites values the draft/user already has. There is no job-title
// field on the SG user account, so seller_title stays manual.
const applyCreateDefaults = (p) => {
  const { firstName = "", lastName = "", name: userName = "" } =
    userDetail?.userInfo || {};
  const fullName = `${firstName || ""} ${lastName || ""}`.trim() || userName || "";
  const next = { ...p };
  if (!next.seller_name && fullName) next.seller_name = fullName;
  if (!next.name && next.product_name) next.name = next.product_name;
  return next;
};

const SellerProfilePage = observer(({ match, location, history }) => {
  const profileId = match?.params?.id || null;
  const isCreate = !profileId;

  // Profile loaded on mount: either existing from store, or auto-fill draft from
  // route state (set by the URL-prompt popup), or an empty shell.
  const [profile, setProfile] = useState(() => {
    if (profileId) {
      const existing = deepResearchStore.sellerProfiles.find(
        (p) => p.id === profileId
      );
      return existing ? hydrateProfileForEdit(existing) : buildEmptyProfile();
    }
    const draft = location?.state?.draft;
    return applyCreateDefaults(
      draft ? hydrateProfileForEdit(draft) : buildEmptyProfile()
    );
  });
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(false);
  // Incrementing this triggers the right-column intel panel to re-derive
  // from the freshly-saved profile (panel watches the prop).
  // Bring the intel panel into view when a derive kicks off — the user just
  // clicked Save at the bottom of a long form, so the panel (and its derive
  // overlay) can be off-screen, especially in the single-column layout.
  const intelRef = useRef(null);

  // Capture the "as-loaded" snapshot for dirty tracking.
  const [baseline, setBaseline] = useState(() => stableJson(profile));

  // Create-flow: userInfo can still be loading on a hard refresh of /new, in
  // which case the useState prefill ran with empty values. Backfill once when
  // it arrives — updating both profile AND baseline so pure prefill never
  // shows the "Unsaved changes" badge.
  useEffect(() => {
    if (profileId) return;
    if (profile.seller_name) return; // already prefilled or user-typed
    const withDefaults = applyCreateDefaults(profile);
    if (withDefaults.seller_name !== profile.seller_name) {
      setProfile(withDefaults);
      setBaseline(stableJson(withDefaults));
    }
  }, [profileId, userDetail?.userInfo?.firstName, userDetail?.userInfo?.lastName, userDetail?.userInfo?.name]);

  // If the store hadn't finished loading when we mounted, watch for the profile
  // to appear and re-hydrate.
  useEffect(() => {
    if (!profileId) return;
    if (deepResearchStore.sellerProfiles.length === 0) {
      deepResearchStore.fetchSellerProfiles({ force: false });
      return;
    }
    const existing = deepResearchStore.sellerProfiles.find(
      (p) => p.id === profileId
    );
    if (existing) {
      const hydrated = hydrateProfileForEdit(existing);
      setProfile(hydrated);
      setBaseline(stableJson(hydrated));
      setNotFound(false);
    } else if (deepResearchStore.sellerProfilesLoaded) {
      setNotFound(true);
    }
  }, [profileId, deepResearchStore.sellerProfiles, deepResearchStore.sellerProfilesLoaded]);

  const dirty = useMemo(
    () => stableJson(profile) !== baseline,
    [profile, baseline]
  );

  const goToList = () => history.push("/settings/seller-profiles");

  const handleBack = () => {
    if (!dirty) {
      goToList();
      return;
    }
    confirmationPopupState.setPopupValues({
      content:
        "Discard your changes? Anything you've entered on this page will be lost.",
      actionBtnText: "Discard changes",
      cancelBtnText: "Keep editing",
      callback: () => {
        confirmationPopupState.setShowPopup(false);
        goToList();
      }
    });
    confirmationPopupState.setShowPopup(true);
  };

  const handleSave = () => {
    setSaving(true);
    const payload = profileId
      ? { ...profile, id: profileId, isActive: true }
      : { ...profile, isActive: true };
    deepResearchStore
      .saveSellerProfile(payload)
      .then((saved) => {
        setSaving(false);
        if (saved && saved.id) {
          toasterState.setToastMsg(
            isCreate
              ? "Profile created. Generating your Seller Intelligence…"
              : "Profile saved.",
            "success"
          );
          if (location?.state?.returnTo) {
            history.push(location.state.returnTo);
            return;
          }
          // Reset baseline against the server response (hydrated) so the dirty
          // check doesn't re-trigger if the backend normalised any field values.
          // For creates: history.replace below causes the useEffect to re-hydrate
          // from the store and call setBaseline there — don't race it by also
          // setting it here (that was the cause of "Unsaved changes" reappearing
          // immediately after a successful save on a newly-created profile).
          if (!isCreate) {
            const savedHydrated = hydrateProfileForEdit(saved);
            setProfile(savedHydrated);
            setBaseline(stableJson(savedHydrated));
          }
          // Intelligence is generated ONCE, right after the profile is created:
          // the URL switches to the new id, the panel loads, finds no
          // intelligence yet and derives it (autoDeriveWhenMissing). An
          // ordinary save never regenerates — the user's edits to the
          // intelligence would be lost; Regenerate on the panel is explicit.
          if (isCreate) {
            history.replace(`/settings/seller-profiles/${saved.id}`);
          }
          if (intelRef.current) {
            intelRef.current.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        } else {
          toasterState.setToastMsg("Could not save seller profile", "failure");
        }
      })
      .catch(() => {
        setSaving(false);
        toasterState.setToastMsg("Could not save seller profile", "failure");
      });
  };

  const headerName =
    profile.name ||
    profile.company_name ||
    profile.product_name ||
    (isCreate ? "New seller profile" : "Untitled profile");

  if (notFound) {
    return (
      <section className="sellerProfilePageWrapper">
        <div className="sellerProfilePageNotFound">
          <h3>Seller profile not found</h3>
          <p>This profile may have been deleted.</p>
          <button className="drPopupBtn primary" onClick={goToList}>
            ← Back to list
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="sellerProfilePageWrapper pdfPrintArea">
      <div className="sellerProfilePageHeader pdfNoPrint">
        <button className="sellerProfileBackBtn" onClick={handleBack}>
          <i className="material-icons">arrow_back</i>
          <span>Back to seller profiles</span>
        </button>
        <div className="sellerProfilePageTitleRow">
          <h2 className="sellerProfilePageTitle">{headerName}</h2>
          {dirty && <span className="sellerProfileDirtyBadge">Unsaved changes</span>}
          {/* Disabled until the profile exists server-side: on the create flow
              there is no saved intelligence to export yet, so the PDF would be
              the typed form and an empty panel. */}
          <PdfDownloadButton
            fileName={`${headerName} - Seller Profile`}
            disabled={isCreate}
            title={
              isCreate
                ? "Save the profile first to download it as a PDF"
                : "Download profile and intelligence as a PDF"
            }
          />
        </div>
      </div>

      {/* PDF-only cover + read-only profile values. The editor below is a form,
          which prints as empty boxes with long textareas clipped, so it is
          replaced rather than printed. */}
      <PdfPrintHeader
        title={headerName}
        subtitle="Seller profile and seller intelligence"
      />
      <SellerProfilePrintDoc profile={profile} />

      <div className="sellerProfilePageBody sellerProfilePageBody--split">
        <div className="sellerProfilePageBody__form pdfNoPrint">
          <SellerProfileFormBody profile={profile} setProfile={setProfile} />
        </div>
        <div className="sellerProfilePageBody__intel" ref={intelRef}>
          <SellerIntelligencePanel
            sellerProfileId={profileId}
            sellerProfileName={headerName}
            autoDeriveWhenMissing
          />
        </div>
      </div>

      <div className="sellerProfilePageFooter pdfNoPrint">
        <button className="drPopupBtn secondary" onClick={handleBack} disabled={saving}>
          Cancel
        </button>
        <button
          className="drPopupBtn primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving
            ? "Saving…"
            : isCreate
            ? "Save Seller Profile"
            : "Save Changes"}
        </button>
      </div>
    </section>
  );
});

export default withRouter(SellerProfilePage);
